/**
 * Google Fonts helper for dynamic font loading and autocomplete search.
 * Uses a curated list of popular Google Fonts to avoid API key requirement.
 */

export interface GoogleFont {
	family: string;
	category: string;
	variants: string[];
}

/**
 * Curated list of ~200 popular Google Fonts sorted by popularity.
 * This avoids the need for a Google Fonts API key.
 */
export const GOOGLE_FONTS: GoogleFont[] = [
	{ family: "Roboto", category: "sans-serif", variants: ["100", "300", "400", "500", "700", "900"] },
	{ family: "Open Sans", category: "sans-serif", variants: ["300", "400", "500", "600", "700", "800"] },
	{ family: "Noto Sans", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Lato", category: "sans-serif", variants: ["100", "300", "400", "700", "900"] },
	{ family: "Montserrat", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Poppins", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Roboto Condensed", category: "sans-serif", variants: ["300", "400", "700"] },
	{ family: "Source Sans Pro", category: "sans-serif", variants: ["200", "300", "400", "600", "700", "900"] },
	{ family: "Oswald", category: "sans-serif", variants: ["200", "300", "400", "500", "600", "700"] },
	{ family: "Roboto Mono", category: "monospace", variants: ["100", "200", "300", "400", "500", "600", "700"] },
	{ family: "Raleway", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Inter", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Nunito Sans", category: "sans-serif", variants: ["200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Ubuntu", category: "sans-serif", variants: ["300", "400", "500", "700"] },
	{ family: "Nunito", category: "sans-serif", variants: ["200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Playfair Display", category: "serif", variants: ["400", "500", "600", "700", "800", "900"] },
	{ family: "Rubik", category: "sans-serif", variants: ["300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Merriweather", category: "serif", variants: ["300", "400", "700", "900"] },
	{ family: "Noto Sans JP", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "PT Sans", category: "sans-serif", variants: ["400", "700"] },
	{ family: "Roboto Slab", category: "serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Work Sans", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Noto Serif", category: "serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Fira Sans", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Kanit", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Quicksand", category: "sans-serif", variants: ["300", "400", "500", "600", "700"] },
	{ family: "Mulish", category: "sans-serif", variants: ["200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Barlow", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "IBM Plex Sans", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700"] },
	{ family: "DM Sans", category: "sans-serif", variants: ["400", "500", "700"] },
	{ family: "Heebo", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Libre Franklin", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Manrope", category: "sans-serif", variants: ["200", "300", "400", "500", "600", "700", "800"] },
	{ family: "Karla", category: "sans-serif", variants: ["200", "300", "400", "500", "600", "700", "800"] },
	{ family: "Titillium Web", category: "sans-serif", variants: ["200", "300", "400", "600", "700", "900"] },
	{ family: "Noto Sans KR", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Josefin Sans", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700"] },
	{ family: "Libre Baskerville", category: "serif", variants: ["400", "700"] },
	{ family: "Arimo", category: "sans-serif", variants: ["400", "500", "600", "700"] },
	{ family: "Source Code Pro", category: "monospace", variants: ["200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Dosis", category: "sans-serif", variants: ["200", "300", "400", "500", "600", "700", "800"] },
	{ family: "PT Serif", category: "serif", variants: ["400", "700"] },
	{ family: "Hind", category: "sans-serif", variants: ["300", "400", "500", "600", "700"] },
	{ family: "Cabin", category: "sans-serif", variants: ["400", "500", "600", "700"] },
	{ family: "Bitter", category: "serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Abel", category: "sans-serif", variants: ["400"] },
	{ family: "Anton", category: "sans-serif", variants: ["400"] },
	{ family: "Source Serif Pro", category: "serif", variants: ["200", "300", "400", "600", "700", "900"] },
	{ family: "Oxygen", category: "sans-serif", variants: ["300", "400", "700"] },
	{ family: "Varela Round", category: "sans-serif", variants: ["400"] },
	{ family: "Archivo", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Overpass", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Crimson Text", category: "serif", variants: ["400", "600", "700"] },
	{ family: "Cairo", category: "sans-serif", variants: ["200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Exo 2", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Asap", category: "sans-serif", variants: ["400", "500", "600", "700"] },
	{ family: "Maven Pro", category: "sans-serif", variants: ["400", "500", "600", "700", "800", "900"] },
	{ family: "Assistant", category: "sans-serif", variants: ["200", "300", "400", "500", "600", "700", "800"] },
	{ family: "Cormorant Garamond", category: "serif", variants: ["300", "400", "500", "600", "700"] },
	{ family: "Dancing Script", category: "handwriting", variants: ["400", "500", "600", "700"] },
	{ family: "Signika", category: "sans-serif", variants: ["300", "400", "500", "600", "700"] },
	{ family: "Catamaran", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Comfortaa", category: "display", variants: ["300", "400", "500", "600", "700"] },
	{ family: "Prompt", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Space Grotesk", category: "sans-serif", variants: ["300", "400", "500", "600", "700"] },
	{ family: "IBM Plex Mono", category: "monospace", variants: ["100", "200", "300", "400", "500", "600", "700"] },
	{ family: "Archivo Narrow", category: "sans-serif", variants: ["400", "500", "600", "700"] },
	{ family: "Barlow Condensed", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Questrial", category: "sans-serif", variants: ["400"] },
	{ family: "Vollkorn", category: "serif", variants: ["400", "500", "600", "700", "800", "900"] },
	{ family: "Public Sans", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Rajdhani", category: "sans-serif", variants: ["300", "400", "500", "600", "700"] },
	{ family: "Shadows Into Light", category: "handwriting", variants: ["400"] },
	{ family: "Teko", category: "sans-serif", variants: ["300", "400", "500", "600", "700"] },
	{ family: "Indie Flower", category: "handwriting", variants: ["400"] },
	{ family: "Yanone Kaffeesatz", category: "sans-serif", variants: ["200", "300", "400", "500", "600", "700"] },
	{ family: "Permanent Marker", category: "handwriting", variants: ["400"] },
	{ family: "Lobster", category: "display", variants: ["400"] },
	{ family: "Pacifico", category: "handwriting", variants: ["400"] },
	{ family: "Fjalla One", category: "sans-serif", variants: ["400"] },
	{ family: "Satisfy", category: "handwriting", variants: ["400"] },
	{ family: "Philosopher", category: "sans-serif", variants: ["400", "700"] },
	{ family: "Acme", category: "sans-serif", variants: ["400"] },
	{ family: "Archivo Black", category: "sans-serif", variants: ["400"] },
	{ family: "Amatic SC", category: "handwriting", variants: ["400", "700"] },
	{ family: "Righteous", category: "display", variants: ["400"] },
	{ family: "Abril Fatface", category: "display", variants: ["400"] },
	{ family: "Russo One", category: "sans-serif", variants: ["400"] },
	{ family: "Caveat", category: "handwriting", variants: ["400", "500", "600", "700"] },
	{ family: "Concert One", category: "display", variants: ["400"] },
	{ family: "Alfa Slab One", category: "display", variants: ["400"] },
	{ family: "Bebas Neue", category: "sans-serif", variants: ["400"] },
	{ family: "Cinzel", category: "serif", variants: ["400", "500", "600", "700", "800", "900"] },
	{ family: "Alegreya", category: "serif", variants: ["400", "500", "600", "700", "800", "900"] },
	{ family: "Fredoka One", category: "display", variants: ["400"] },
	{ family: "Kalam", category: "handwriting", variants: ["300", "400", "700"] },
	{ family: "Patua One", category: "display", variants: ["400"] },
	{ family: "Great Vibes", category: "handwriting", variants: ["400"] },
	{ family: "Signika Negative", category: "sans-serif", variants: ["300", "400", "500", "600", "700"] },
	{ family: "Staatliches", category: "display", variants: ["400"] },
	{ family: "Zilla Slab", category: "serif", variants: ["300", "400", "500", "600", "700"] },
	{ family: "Tajawal", category: "sans-serif", variants: ["200", "300", "400", "500", "700", "800", "900"] },
	{ family: "Play", category: "sans-serif", variants: ["400", "700"] },
	{ family: "Didact Gothic", category: "sans-serif", variants: ["400"] },
	{ family: "Cookie", category: "handwriting", variants: ["400"] },
	{ family: "Courgette", category: "handwriting", variants: ["400"] },
	{ family: "Kaushan Script", category: "handwriting", variants: ["400"] },
	{ family: "Sacramento", category: "handwriting", variants: ["400"] },
	{ family: "Yellowtail", category: "handwriting", variants: ["400"] },
	{ family: "Gloria Hallelujah", category: "handwriting", variants: ["400"] },
	{ family: "Patrick Hand", category: "handwriting", variants: ["400"] },
	{ family: "Orbitron", category: "sans-serif", variants: ["400", "500", "600", "700", "800", "900"] },
	{ family: "Bangers", category: "display", variants: ["400"] },
	{ family: "Bree Serif", category: "serif", variants: ["400"] },
	{ family: "Crete Round", category: "serif", variants: ["400"] },
	{ family: "Special Elite", category: "display", variants: ["400"] },
	{ family: "Neuton", category: "serif", variants: ["200", "300", "400", "700", "800"] },
	{ family: "Rokkitt", category: "serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Spectral", category: "serif", variants: ["200", "300", "400", "500", "600", "700", "800"] },
	{ family: "Lora", category: "serif", variants: ["400", "500", "600", "700"] },
	{ family: "EB Garamond", category: "serif", variants: ["400", "500", "600", "700", "800"] },
	{ family: "Nanum Gothic", category: "sans-serif", variants: ["400", "700", "800"] },
	{ family: "Pathway Gothic One", category: "sans-serif", variants: ["400"] },
	{ family: "Saira Condensed", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Advent Pro", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700"] },
	{ family: "Chakra Petch", category: "sans-serif", variants: ["300", "400", "500", "600", "700"] },
	{ family: "Sarabun", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800"] },
	{ family: "Chivo", category: "sans-serif", variants: ["300", "400", "700", "900"] },
	{ family: "Economica", category: "sans-serif", variants: ["400", "700"] },
	{ family: "Encode Sans", category: "sans-serif", variants: ["100", "200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Antic Slab", category: "serif", variants: ["400"] },
	{ family: "Old Standard TT", category: "serif", variants: ["400", "700"] },
	{ family: "Noticia Text", category: "serif", variants: ["400", "700"] },
	{ family: "Gentium Book Basic", category: "serif", variants: ["400", "700"] },
	{ family: "Domine", category: "serif", variants: ["400", "500", "600", "700"] },
	{ family: "Amiri", category: "serif", variants: ["400", "700"] },
	{ family: "Sanchez", category: "serif", variants: ["400"] },
	{ family: "Literata", category: "serif", variants: ["200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Fira Code", category: "monospace", variants: ["300", "400", "500", "600", "700"] },
	{ family: "JetBrains Mono", category: "monospace", variants: ["100", "200", "300", "400", "500", "600", "700", "800"] },
	{ family: "Inconsolata", category: "monospace", variants: ["200", "300", "400", "500", "600", "700", "800", "900"] },
	{ family: "Space Mono", category: "monospace", variants: ["400", "700"] },
	{ family: "Courier Prime", category: "monospace", variants: ["400", "700"] },
	{ family: "Anonymous Pro", category: "monospace", variants: ["400", "700"] },
	{ family: "Ubuntu Mono", category: "monospace", variants: ["400", "700"] },
	{ family: "System Default", category: "system", variants: ["400"] }
];

// Track loaded fonts to avoid duplicate loading
const loadedFonts = new Set<string>();

/**
 * List of common system fonts that should not be loaded from Google Fonts.
 * These fonts are available on the user's system and don't need to be loaded.
 */
const SYSTEM_FONTS = [
	"System Default",
	"system-ui",
	"SegoeUI",
	"Segoe UI",
	"Arial",
	"Helvetica",
	"Times New Roman",
	"Times",
	"Courier New",
	"Courier",
	"Verdana",
	"Georgia",
	"Palatino",
	"Garamond",
	"Bookman",
	"Comic Sans MS",
	"Trebuchet MS",
	"Arial Black",
	"Impact",
	"Tahoma",
	"Lucida Console",
	"Lucida Sans Unicode",
	"MS Sans Serif",
	"MS Serif",
	"Symbol",
	"Webdings",
	"Wingdings"
];

/**
 * Checks if a font is a system font that shouldn't be loaded from Google Fonts.
 */
function isSystemFont(fontFamily: string): boolean {
	const normalized = fontFamily.toLowerCase().trim();
	return SYSTEM_FONTS.some((systemFont) => systemFont.toLowerCase() === normalized);
}

/**
 * Loads a Google Font dynamically by injecting a <link> tag.
 * @param fontFamily - The font family name (e.g., "Roboto", "Open Sans")
 * @param weights - Optional array of weights to load (defaults to 400, 500, 700)
 */
export function loadGoogleFont(fontFamily: string, weights: string[] = ["400", "500", "700"]): Promise<void> {
	return new Promise((resolve) => {
		// Skip if already loaded or system font
		if (loadedFonts.has(fontFamily) || isSystemFont(fontFamily)) {
			if (isSystemFont(fontFamily)) {
				// Log debug message for system fonts instead of error
				console.debug(`[GoogleFonts] Skipping system font: ${fontFamily}`);
			}
			resolve();
			return;
		}

		// Check if font exists in our list
		const fontInfo = GOOGLE_FONTS.find((f) => f.family.toLowerCase() === fontFamily.toLowerCase());
		if (!fontInfo) {
			// Try to load it anyway (might be a valid Google Font not in our list)
			console.log(`[GoogleFonts] Font "${fontFamily}" not in curated list, attempting to load anyway`);
		}

		// Filter weights to only those available
		const availableWeights = fontInfo ? weights.filter((w) => fontInfo.variants.includes(w)) : weights;
		const weightsParam = availableWeights.length > 0 ? availableWeights.join(";") : "400";

		// Create the Google Fonts URL
		const encodedFamily = encodeURIComponent(fontFamily);
		const url = `https://fonts.googleapis.com/css2?family=${encodedFamily}:wght@${weightsParam}&display=swap`;

		// Check if link already exists
		const existingLink = document.querySelector(`link[href*="${encodedFamily}"]`);
		if (existingLink) {
			loadedFonts.add(fontFamily);
			resolve();
			return;
		}

		// Create and inject the link tag
		const link = document.createElement("link");
		link.rel = "stylesheet";
		link.href = url;

		link.onload = () => {
			loadedFonts.add(fontFamily);
			console.log(`[GoogleFonts] Loaded font: ${fontFamily}`);
			resolve();
		};

		link.onerror = () => {
			console.error(`[GoogleFonts] Failed to load font: ${fontFamily}`);
			resolve(); // Resolve anyway to not block the app
		};

		document.head.appendChild(link);
	});
}

/**
 * Searches the font list and returns matching fonts.
 * @param query - Search query string
 * @param limit - Maximum number of results (default 20)
 */
export function searchFonts(query: string, limit: number = 20): GoogleFont[] {
	if (!query || query.trim() === "") {
		// Return popular fonts when no query
		return GOOGLE_FONTS.slice(0, limit);
	}

	const lowerQuery = query.toLowerCase().trim();

	// Score and sort by relevance
	const scored = GOOGLE_FONTS.map((font) => {
		const lowerFamily = font.family.toLowerCase();
		let score = 0;

		// Exact match gets highest score
		if (lowerFamily === lowerQuery) {
			score = 1000;
		}
		// Starts with query gets high score
		else if (lowerFamily.startsWith(lowerQuery)) {
			score = 100;
		}
		// Contains query gets medium score
		else if (lowerFamily.includes(lowerQuery)) {
			score = 10;
		}
		// Word starts with query
		else if (lowerFamily.split(" ").some((word) => word.startsWith(lowerQuery))) {
			score = 50;
		}

		return { font, score };
	});

	return scored
		.filter((item) => item.score > 0)
		.sort((a, b) => b.score - a.score)
		.slice(0, limit)
		.map((item) => item.font);
}

/**
 * Gets the font category for fallback styling
 */
export function getFontCategory(fontFamily: string): string {
	const font = GOOGLE_FONTS.find((f) => f.family.toLowerCase() === fontFamily.toLowerCase());
	return font?.category || "sans-serif";
}

/**
 * Returns font items formatted for v-autocomplete
 */
export function getFontItems(): Array<{ text: string; value: string; category: string }> {
	return GOOGLE_FONTS.map((font) => ({
		text: font.family,
		value: font.family,
		category: font.category
	}));
}
